import { supabase } from '../supabaseClient';
import type { Routine, Workout, TrainingPlan, User, ExerciseCatalogEntry } from '../types';
import { 
  getEnvNumber, 
  sanitizeCsvField, 
  normalizeText, 
  clampNonNegativeNumber 
} from '../utils/dataSanitization';
import { getLocalDateString } from '../utils/dateUtils';

export function useDataImportExport(
  user: User | null,
  routines: Routine[],
  workouts: Workout[],
  trainingPlans: TrainingPlan[],
  ensureExerciseEntry: (name: string) => Promise<ExerciseCatalogEntry>,
  loadData: () => Promise<void>,
  showNotification: (msg: string, type: 'success' | 'error') => void,
  setShowSettings: (show: boolean) => void
) {
  const MAX_IMPORT_ROUTINES = getEnvNumber(process.env.REACT_APP_IMPORT_MAX_ROUTINES, 200);
  const MAX_IMPORT_WORKOUTS = getEnvNumber(process.env.REACT_APP_IMPORT_MAX_WORKOUTS, 500);
  const MAX_IMPORT_EXERCISES = getEnvNumber(process.env.REACT_APP_IMPORT_MAX_EXERCISES, 50);
  const MAX_IMPORT_SETS = getEnvNumber(process.env.REACT_APP_IMPORT_MAX_SETS, 50);
  const MAX_IMPORT_PLANS = 50;
  const VALID_PLAN_STATUSES = new Set(['draft', 'active', 'completed', 'abandoned']);
  const VALID_DURATION_WEEKS = new Set([4, 6, 8, 10, 12, 14]);

  const exportToJSON = () => {
    const data = { routines, workouts, trainingPlans, exportDate: new Date().toISOString() };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `gym-backup-${getLocalDateString()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportToCSV = () => {
    let csv = 'Fecha,Rutina,Ejercicio,Serie,Peso(kg),Repeticiones\n';
    workouts.forEach((w) => {
      w.exercises.forEach((ex) => {
        ex.sets.forEach((set, index) => {
          csv += `${sanitizeCsvField(w.date)},${sanitizeCsvField(w.routine_name)},${sanitizeCsvField(ex.name)},${sanitizeCsvField(index + 1)},${sanitizeCsvField(set.weight)},${sanitizeCsvField(set.reps)}\n`;
        });
      });
    });
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `gym-workouts-${getLocalDateString()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const importFromJSON = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !user) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const data = JSON.parse(e.target?.result as string);
        const safeRoutines = Array.isArray(data?.routines) ? data.routines : [];
        const safeWorkouts = Array.isArray(data?.workouts) ? data.workouts : [];
        const safePlans = Array.isArray(data?.trainingPlans) ? data.trainingPlans : [];
        let wasTruncated = false;

        // Map old routine IDs to new ones (for plan day references)
        const routineIdMap = new Map<string, string>();

        // Import Routines
        if (safeRoutines.length > 0) {
          if (safeRoutines.length > MAX_IMPORT_ROUTINES) wasTruncated = true;
          for (const r of safeRoutines.slice(0, MAX_IMPORT_ROUTINES)) {
            const name = normalizeText(r?.name, 80);
            if (!name) continue;
            const oldId = r?.id;
            const { data: row, error } = await supabase.from('routines').insert({ user_id: user.id, name }).select('id').single();
            if (error) throw error;
            if (oldId) routineIdMap.set(oldId, row.id);

            const exercises = Array.isArray(r?.exercises) ? r.exercises : [];
            if (exercises.length > MAX_IMPORT_EXERCISES) wasTruncated = true;
            const limitedExs = exercises.slice(0, MAX_IMPORT_EXERCISES);
            const payload = [];
            for (let i = 0; i < limitedExs.length; i++) {
              const exName = normalizeText(limitedExs[i]?.name, 50);
              if (!exName) continue;
              const entry = await ensureExerciseEntry(exName);
              payload.push({
                routine_id: row.id,
                exercise_id: entry.id,
                order_index: i,
                default_sets: clampNonNegativeNumber(limitedExs[i]?.sets ?? 3),
                default_reps: clampNonNegativeNumber(limitedExs[i]?.reps ?? 10),
              });
            }
            if (payload.length > 0) {
              const { error: relError } = await supabase.from('routine_exercises').insert(payload);
              if (relError) throw relError;
            }
          }
        }

        // Track workouts that had a training_plan_id for linking after plans are imported
        // Maps new workout ID → old training_plan_id
        const workoutPlanLinks = new Map<string, string>();

        // Import Workouts
        if (safeWorkouts.length > 0) {
          if (safeWorkouts.length > MAX_IMPORT_WORKOUTS) wasTruncated = true;
          for (const w of safeWorkouts.slice(0, MAX_IMPORT_WORKOUTS)) {
            const name = normalizeText(w?.routineName || w?.routine_name || w?.routine_name_snapshot, 80) || 'Entrenamiento';
            const { data: row, error } = await supabase.from('workouts').insert({
              user_id: user.id,
              routine_name_snapshot: name,
              date: typeof w?.date === 'string' ? w.date : getLocalDateString(),
            }).select('id').single();
            if (error) throw error;

            if (typeof w?.training_plan_id === 'string' && w.training_plan_id) {
              workoutPlanLinks.set(row.id, w.training_plan_id);
            }

            const exs = Array.isArray(w?.exercises) ? w.exercises : [];
            if (exs.length > MAX_IMPORT_EXERCISES) wasTruncated = true;
            const limitedExs = exs.slice(0, MAX_IMPORT_EXERCISES);
            const exPayload = [];
            for (let i = 0; i < limitedExs.length; i++) {
              const exName = normalizeText(limitedExs[i]?.name, 50);
              if (!exName) continue;
              const entry = await ensureExerciseEntry(exName);
              exPayload.push({ workout_id: row.id, exercise_id: entry.id, exercise_name_snapshot: exName, order_index: i });
            }

            if (exPayload.length > 0) {
              const { data: exRows, error: exRelError } = await supabase.from('workout_exercises').insert(exPayload).select('id, order_index');
              if (exRelError) throw exRelError;
              
              const setsPayload: any[] = [];
              for (const exRow of (exRows || [])) {
                const ex = limitedExs[exRow.order_index];
                if (!Array.isArray(ex?.sets)) continue;
                if (ex.sets.length > MAX_IMPORT_SETS) wasTruncated = true;
                
                const limitedSets = ex.sets.slice(0, MAX_IMPORT_SETS);
                for (let setIdx = 0; setIdx < limitedSets.length; setIdx++) {
                  const set = limitedSets[setIdx];
                  setsPayload.push({
                    workout_exercise_id: exRow.id,
                    set_index: setIdx,
                    weight: clampNonNegativeNumber(set?.weight ?? 0),
                    reps: clampNonNegativeNumber(set?.reps ?? 0),
                  });
                }
              }
              if (setsPayload.length > 0) {
                const { error: setRelError } = await supabase.from('workout_sets').insert(setsPayload);
                if (setRelError) throw setRelError;
              }
            }
          }
        }

        // Import Training Plans
        const planIdMap = new Map<string, string>();

        if (safePlans.length > 0) {
          if (safePlans.length > MAX_IMPORT_PLANS) wasTruncated = true;
          for (const p of safePlans.slice(0, MAX_IMPORT_PLANS)) {
            const name = normalizeText(p?.name, 50);
            if (!name) continue;

            const oldPlanId = p?.id;
            const daysPerWeek = Math.max(1, Math.min(6, Number(p?.days_per_week) || 3));
            const durationWeeks = VALID_DURATION_WEEKS.has(Number(p?.duration_weeks))
              ? Number(p.duration_weeks)
              : 8;
            // Import non-active statuses as-is; never import an 'active' plan (could conflict with existing)
            const rawStatus = typeof p?.status === 'string' && VALID_PLAN_STATUSES.has(p.status) ? p.status : 'draft';
            const status = rawStatus === 'active' ? 'draft' : rawStatus;

            const { data: planRow, error: planError } = await supabase
              .from('training_plans')
              .insert({
                user_id: user.id,
                name,
                days_per_week: daysPerWeek,
                duration_weeks: durationWeeks,
                status,
                start_date: status !== 'draft' && typeof p?.start_date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(p.start_date) && !isNaN(Date.parse(p.start_date)) ? p.start_date : null,
              })
              .select('id')
              .single();
            if (planError) throw planError;
            if (oldPlanId) planIdMap.set(oldPlanId, planRow.id);

            const days = Array.isArray(p?.days) ? p.days : [];
            const daysPayload = [];
            for (const d of days) {
              const dayNumber = Number(d?.day_number);
              if (!dayNumber || dayNumber < 1) continue;

              // Resolve routine_id: map old→new, skip if unmapped
              const oldRoutineId = d?.routine_id;
              const newRoutineId = oldRoutineId ? routineIdMap.get(oldRoutineId) : undefined;
              if (!newRoutineId) continue;

              daysPayload.push({
                plan_id: planRow.id,
                day_number: dayNumber,
                routine_id: newRoutineId,
                label: normalizeText(d?.label, 30) || null,
              });
            }

            if (daysPayload.length > 0) {
              const { error: daysError } = await supabase.from('training_plan_days').insert(daysPayload);
              if (daysError) throw daysError;
            }
          }
        }

        // Restore workout → plan associations
        if (workoutPlanLinks.size > 0 && planIdMap.size > 0) {
          for (const [newWorkoutId, oldPlanId] of workoutPlanLinks) {
            const newPlanId = planIdMap.get(oldPlanId);
            if (!newPlanId) continue;
            const { error: linkError } = await supabase
              .from('workouts')
              .update({ training_plan_id: newPlanId })
              .eq('id', newWorkoutId);
            if (linkError) throw linkError;
          }
        }

        await loadData();
        showNotification(wasTruncated ? 'Datos importados con límites.' : 'Datos importados', 'success');
        setShowSettings(false);
      } catch (error) {
        showNotification('Error al importar archivo JSON', 'error');
        console.error(error);
      }
    };
    reader.readAsText(file);
    event.target.value = '';
  };

  return { exportToJSON, exportToCSV, importFromJSON };
}
